import { useEffect, useRef, useState } from "react";

import { textFieldFilterValidator } from "@asd/customer-portal-ui";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton, InputAdornment, TextField, Tooltip, useTheme } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

type FilterTextFieldParams = {
  filters: { [key: string]: any };
  setFilters: (_filter: { key: any; value: any }) => void;
  filterKey: string;
  fullHeight?: boolean;
  [rest: string]: any;
};

const FilterTextField = ({ filters, setFilters, filterKey, fullHeight, ...rest }: FilterTextFieldParams) => {
  const theme = useTheme();

  const [isFocused, setIsFocused] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const onChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    control,
    register,
    formState: { isValid },
    watch,
  } = useForm({
    defaultValues: {
      field: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const fieldValue = watch("field");

  useEffect(() => {
    if (onChangeTimeoutRef.current) {
      clearTimeout(onChangeTimeoutRef.current);
    }

    if (!fieldValue?.length) {
      setFilters({
        key: filterKey,
        value: "",
      });
    } else {
      if (isValid) {
        onChangeTimeoutRef.current = setTimeout(() => {
          setFilters({
            key: filterKey,
            value: fieldValue,
          });
        }, 500);
      }
    }
  }, [fieldValue, filterKey, isValid, setFilters]);

  return (
    <Controller
      control={control}
      defaultValue=""
      name="field"
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...register("field", textFieldFilterValidator)}
          {...field}
          error={!!error}
          helperText={error ? error.message : null}
          onBlur={() => {
            setIsFocused(false);
          }}
          onChange={(event) => {
            field.onChange(event);
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onMouseEnter={() => {
            setIsHovering(true);
          }}
          onMouseLeave={() => {
            setIsHovering(false);
          }}
          size="small"
          slotProps={{
            input: {
              endAdornment:
                !isHovering || (isValid && !filters[filterKey]?.length) ? null : (
                  <InputAdornment position="end">
                    <Tooltip title="Clear">
                      <IconButton
                        onClick={(event) => {
                          field.onChange(event);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
            },
          }}
          {...rest}
          sx={[
            {
              width: "50%",
              "& .MuiInputBase-root": {
                height: "100%",
              },
            },
            !fullHeight && {
              input: {
                py: 0.6,
              },
            },
            !fullHeight &&
              !isFocused &&
              isValid &&
              !filters[filterKey]?.length && {
                label: {
                  transform: `translate(${theme.spacing(1.5)}, ${theme.spacing(0.6)})`,
                },
              },
            { ...rest.sx },
          ]}
        />
      )}
    />
  );
};

export default FilterTextField;
